import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmailIcon from '@material-ui/icons/Email';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  submit: {
    marginTop: theme.spacing(1),
    height: 48,
    fontSize: '1rem',
    fontWeight: 600,
  },
  forgotPassword: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  error: {
    backgroundColor: 'rgba(239,68,68,0.08)',
    color: '#b91c1c',
    border: '1px solid rgba(239,68,68,0.25)',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: '0.9rem',
  },
  hint: {
    marginTop: theme.spacing(2),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

const LoginPage = () => {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const msg =
        (err && err.message) ||
        'Échec de la connexion. Vérifiez vos identifiants.';
      setError(msg);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box width="100%">
      <form className={classes.form} onSubmit={handleSubmit} noValidate>
        <TextField
          variant="outlined"
          required
          fullWidth
          id="email"
          label="Adresse email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon fontSize="small" style={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          required
          fullWidth
          name="password"
          label="Mot de passe"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon
                  fontSize="small"
                  style={{ color: '#94a3b8' }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label="Afficher/masquer le mot de passe"
                >
                  {showPassword ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Box className={classes.error} role="alert">
            {error}
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} style={{ color: '#fff' }} />
          ) : (
            'Se connecter'
          )}
        </Button>

        <Box className={classes.forgotPassword}>
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            Mot de passe oublié ?
          </Link>
        </Box>

        <Typography className={classes.hint}>
          Accès réservé aux administrateurs autorisés.
        </Typography>
      </form>
    </Box>
  );
};

export default LoginPage;
